// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

export interface LOATemplate {
  id?: string;
  name: string;
  code: string;
  description?: string;
  fileUrl?: string;
  fileSize?: number;
  variables?: Record<string, unknown>;
  version?: number;
  isDefault?: boolean;
  resolutionSource?: string;
}

export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
  first: boolean;
  last: boolean;
}

const API_BASE = '/api/loa-templates';

export const templateApi = {
  async getAll(params?: {
    search?: string;
    page?: number;
    size?: number;
  }): Promise<PageResponse<LOATemplate>> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.page !== undefined) queryParams.append('page', String(params.page));
    if (params?.size !== undefined) queryParams.append('size', String(params.size));

    const queryString = queryParams.toString();
    const suffix = queryString ? `?${queryString}` : '';
    const url = `${API_BASE}${suffix}`;
    const response = await fetch(url, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch templates');
    }
    const data = await response.json();
    // Return full paginated response
    return data;
  },

  async getById(id: string): Promise<LOATemplate> {
    const response = await fetch(`${API_BASE}/${id}`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch template');
    }
    return response.json();
  },

  async upload(file: File, metadata: Omit<LOATemplate, 'id' | 'fileUrl' | 'fileSize' | 'version'>): Promise<LOATemplate> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', metadata.name);
    formData.append('code', metadata.code);
    if (metadata.description) {
      formData.append('description', metadata.description);
    }
    if (metadata.isDefault !== undefined) {
      formData.append('isDefault', String(metadata.isDefault));
    }

    const response = await fetch(`${API_BASE}`, {
      method: 'POST',
      credentials: 'include',
      body: formData
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || 'Failed to upload template');
    }
    return response.json();
  },

  async updateMetadata(id: string, metadata: Partial<LOATemplate>): Promise<LOATemplate> {
    const response = await fetch(`${API_BASE}/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(metadata)
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || 'Failed to update template metadata');
    }
    return response.json();
  },

  async delete(id: string): Promise<void> {
    const response = await fetch(`${API_BASE}/${id}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to delete template');
    }
  },

  async download(id: string): Promise<Blob> {
    const response = await fetch(`${API_BASE}/${id}/download`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to download template');
    }
    return response.blob();
  }
};
